(() => {
  'use strict';

  const root = document.querySelector('[data-trm-suspension]');
  if (!root) return;

  const GARAGE_KEY = 'trmSelectedVehicleV1';
  const baseShockIndex = Array.isArray(window.TRM_SHOCK_INDEX_V20) ? window.TRM_SHOCK_INDEX_V20 : [];
  let index = [...baseShockIndex];
  const liftIndex = Array.isArray(window.TRM_LIFT_INDEX_V22?.products) ? window.TRM_LIFT_INDEX_V22.products : [];
  const form = root.querySelector('[data-trm-suspension-form]');
  const strategy = root.querySelector('[data-trm-suspension-strategy]');
  const results = root.querySelector('[data-trm-shock-results]');
  let activeMode = 'system';
  let vehicleCatalogKey = '';
  let vehicleCatalogPromise = null;
  let vehicleCatalogFullPromise = null;

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const normalize = (value) => String(value || '')
    .toLowerCase()
    .replace(/chevrolet/g, 'chevy')
    .replace(/f[-\s]?(\d{3})/g, 'f $1')
    .replace(/[^a-z0-9.]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const uniqueCatalogMatches = (matches) => {
    const seen = new Set();
    return matches.filter((match) => {
      const key = `${normalize(match.product?.vendor)}|${normalize(match.product?.title)}`;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const readVehicle = () => {
    try { return JSON.parse(window.localStorage.getItem(GARAGE_KEY) || 'null'); }
    catch (error) { return null; }
  };

  const exactYearFrom = (vehicle) => {
    const candidate = String(vehicle?.exactYear || vehicle?.exact_year || vehicle?.modelYear || vehicle?.model_year || vehicle?.year || '');
    return /^(?:19|20)\d{2}$/.test(candidate) ? candidate : '';
  };

  const drivetrainFrom = (vehicle) => {
    const raw = normalize(vehicle?.drivetrain || vehicle?.drive || vehicle?.fitmentDrive || vehicle?.fitment_drive || '');
    if (/\b(?:4wd|awd|4x4|four wheel drive)\b/.test(raw)) return '4wd';
    if (/\b(?:2wd|rwd|4x2|two wheel drive)\b/.test(raw)) return '2wd';
    return raw === 'unknown' ? 'unknown' : '';
  };

  const saveVehicleDetail = (detail) => {
    const vehicle = readVehicle();
    if (!vehicle) return;
    try {
      window.localStorage.setItem(GARAGE_KEY, JSON.stringify({ ...vehicle, ...detail }));
      window.dispatchEvent(new CustomEvent('trm:vehicle-detail-changed', { detail }));
    } catch (error) {}
  };

  const vehicleLabel = (vehicle) => vehicle?.label || 'Choose your truck for vehicle-specific results';

  const yearsForVehicle = (vehicle) => {
    const raw = String(`${vehicle?.key || ''} ${vehicle?.label || ''}`).toLowerCase();
    const range = raw.match(/\b((?:19|20)\d{2})\s*(?:-|–|to)\s*((?:19|20)\d{2})\b/);
    if (range) {
      const min = Number(range[1]);
      const max = Math.min(Number(range[2]), new Date().getFullYear() + 1);
      return Array.from({ length:Math.max(0, max-min+1) }, (_, index) => min + index);
    }
    const plus = raw.match(/\b((?:19|20)\d{2})(?:\s*\+(?=\s|$)|[-\s]+plus\b)/);
    if (plus) {
      const min = Number(plus[1]);
      const max = new Date().getFullYear() + 1;
      return Array.from({ length:Math.max(0, max-min+1) }, (_, index) => min + index);
    }
    const single = raw.match(/\b((?:19|20)\d{2})\b/);
    return single ? [Number(single[1])] : [];
  };

  const vehicleSuspensionUrl = (vehicle) => vehicle?.key
    ? `/collections/${String(vehicle.key).replace(/^vehicle:/, '')}-suspension`
    : '/collections/truck-suspension-lift-kits';

  const collectionHandle = (vehicle) => vehicle?.key ? `${String(vehicle.key).replace(/^vehicle:/, '')}-suspension` : '';

  const setCatalogStatus = (copy = '', state = '') => {
    const status = root.querySelector('[data-trm-suspension-catalog-status]');
    if (!status) return;
    status.textContent = copy;
    status.classList.toggle('is-loading', state === 'loading');
    status.classList.toggle('is-error', state === 'error');
  };

  const isShockProduct = (product) => {
    const type = normalize(product?.product_type || '');
    const title = normalize(product?.title || '');
    const supportedType = /shock absorber|shocks and struts|shock spring kits|suspension strut and coil spring assembly|coilover spring and shock assembly|^coilovers?$/.test(type);
    if (/\b(?:lift|lowering) kit\b|\bstage \d+ suspension system\b/.test(title) && !/shocks only|shock package|loaded strut pair|coilovers? - (?:front|rear)/.test(title)) return false;
    if (!supportedType && (!/shock|strut|coilover|damper/.test(title) || /lift kit|lowering kit/.test(type))) return false;
    const raw = `${type} ${title}`;
    if (/bushing|boot|bracket|mounting kit|extension kit|hardware|spacer|component/.test(raw) && !/shock absorber|shock set|rear shocks|front shocks|loaded strut|leveling strut|coilover spring and shock/.test(raw)) return false;
    return true;
  };

  const normalizeStorefrontProduct = (product, scopeKey) => {
    const variant = (product.variants || []).find((item) => item.available) || product.variants?.[0] || {};
    const image = product.images?.[0]?.src || product.featured_image || '';
    return {
      id: String(product.id || ''), title: product.title || '', handle: product.handle || '',
      url: `/products/${product.handle || ''}`, vendor: product.vendor || '', productType: product.product_type || '',
      price: Number(variant.price || 0), currency: 'USD', inventory: variant.available ? 1 : 0,
      image, description: product.body_html ? String(product.body_html).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 800) : '',
      tags: Array.isArray(product.tags) ? product.tags : [], sku: variant.sku || '', vehicleCollection: scopeKey
    };
  };

  const loadVehicleCatalog = (vehicle, announce = false, complete = false) => {
    const handle = collectionHandle(vehicle);
    if (!handle) return Promise.resolve([]);
    if (vehicleCatalogKey === handle && vehicleCatalogPromise) {
      if (announce) setCatalogStatus(complete ? 'Checking every remaining saved-truck listing…' : 'Loading fitment-ranked listings for this truck…', 'loading');
      return complete
        ? vehicleCatalogPromise.then(() => vehicleCatalogFullPromise || [])
        : vehicleCatalogPromise;
    }
    vehicleCatalogKey = handle;
    index = [...baseShockIndex];
    vehicleCatalogFullPromise = null;
    if (announce) setCatalogStatus('Loading fitment-ranked listings for this truck…', 'loading');
    vehicleCatalogPromise = (async () => {
      const readPage = async (page) => {
        const response = await fetch(`/collections/${encodeURIComponent(handle)}/products.json?limit=250&page=${page}`, { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`Catalog request failed (${response.status})`);
        const payload = await response.json();
        return Array.isArray(payload.products) ? payload.products : [];
      };
      const select = (products) => products.filter(isShockProduct).flatMap((product) => {
          const normalized = normalizeStorefrontProduct(product, handle);
          return normalized.inventory > 0 ? [normalized] : [];
        });
      const merge = (products) => {
        const byHandle = new Map([...index, ...products].map((product) => [product.handle || product.id, product]));
        index = [...byHandle.values()];
      };
      const firstPage = await readPage(1);
      const priority = select(firstPage);
      if (vehicleCatalogKey !== handle) return [];
      merge(priority);
      setCatalogStatus(`${priority.length} prioritized available listings loaded; the rest of this truck catalog is checking in the background.`);
      if (firstPage.length === 250) {
        vehicleCatalogFullPromise = (async () => {
          const additional = [];
          for (let page = 2; page <= 8; page += 1) {
            const products = await readPage(page);
            additional.push(...select(products));
            if (products.length < 250) break;
          }
          if (vehicleCatalogKey !== handle) return [];
          merge(additional);
          const total = priority.length + additional.length;
          setCatalogStatus(`${total} available shock, strut and coilover listings loaded for this saved truck.`);
          return [...priority, ...additional];
        })().catch(() => {
          if (vehicleCatalogKey === handle) setCatalogStatus('Priority listings are ready; the remaining catalog pages could not be refreshed.', 'error');
          return priority;
        });
      } else {
        vehicleCatalogFullPromise = Promise.resolve(priority);
        setCatalogStatus(`${priority.length} available shock, strut and coilover listings loaded for this saved truck.`);
      }
      return complete ? vehicleCatalogFullPromise : priority;
    })().catch((error) => {
      if (vehicleCatalogKey === handle) {
        index = [...baseShockIndex];
        setCatalogStatus('The live vehicle catalog could not be loaded. Results remain locked to the verified fallback index.', 'error');
      }
      return [];
    });
    return vehicleCatalogPromise;
  };

  const syncVehicle = () => {
    const vehicle = readVehicle();
    const label = root.querySelector('[data-trm-suspension-vehicle]');
    const help = root.querySelector('[data-trm-suspension-vehicle-help]');
    const button = root.querySelector('[data-trm-suspension-garage-open]');
    if (label) label.textContent = vehicleLabel(vehicle);
    if (help) help.textContent = vehicle
      ? 'Loaded from My Truck Garage. Change it here without leaving the guide.'
      : 'Choose once and every TRM guide can reuse the vehicle.';
    if (button) button.textContent = vehicle ? 'Change truck' : 'Choose truck';
    const year = root.querySelector('[data-trm-suspension-exact-year]');
    const drive = root.querySelector('[name="fitment_drive"]');
    const yearField = root.querySelector('[data-trm-suspension-year-field]');
    const driveField = root.querySelector('[data-trm-suspension-drive-field]');
    const checkpoint = root.querySelector('[data-trm-suspension-checkpoint]');
    const checkpointCopy = root.querySelector('[data-trm-suspension-checkpoint-copy]');
    if (year) {
      const previous = year.value;
      const years = yearsForVehicle(vehicle);
      const saved = exactYearFrom(vehicle);
      year.replaceChildren(new Option(years.length ? 'Choose exact model year' : 'Choose your truck first', ''));
      years.forEach((value) => year.add(new Option(String(value), String(value))));
      year.disabled = !years.length;
      if (years.map(String).includes(saved)) year.value = saved;
      else if (years.map(String).includes(previous)) year.value = previous;
      else if (years.length === 1) year.value = String(years[0]);
      if (yearField) yearField.hidden = Boolean(saved && year.value === saved);
    }
    const savedDrive = drivetrainFrom(vehicle);
    if (drive && savedDrive && [...drive.options].some((option) => option.value === savedDrive)) drive.value = savedDrive;
    if (driveField) driveField.hidden = Boolean(savedDrive && drive?.value === savedDrive);
    const yearReady = Boolean(year?.value);
    const driveReady = Boolean(drive?.value);
    if (checkpoint) checkpoint.classList.toggle('is-complete', Boolean(vehicle && yearReady && driveReady));
    if (checkpointCopy) checkpointCopy.textContent = !vehicle
      ? 'Choose a truck; we will ask only for missing fitment details.'
      : yearReady && driveReady
        ? `${year.value} and ${drive.value.toUpperCase()} loaded from My Truck Garage—nothing to enter again.`
        : `My Truck Garage is connected. Complete only ${!yearReady && !driveReady ? 'the exact model year and drivetrain' : !yearReady ? 'the missing exact model year' : 'the missing drivetrain'}.`;
    const browseUrl = vehicleSuspensionUrl(vehicle);
    root.querySelectorAll('[data-trm-suspension-browse],[data-trm-shock-browse]').forEach((link) => { link.href = browseUrl; });
    if (vehicle) loadVehicleCatalog(vehicle, false);
    else {
      vehicleCatalogKey = '';
      vehicleCatalogPromise = null;
      vehicleCatalogFullPromise = null;
      index = [...baseShockIndex];
      setCatalogStatus('Product matching stays locked until a truck is selected.');
    }
  };

  const enablePanelFields = () => {
    root.querySelectorAll('[data-trm-mode-panel]').forEach((panel) => {
      const isActive = panel.dataset.trmModePanel === activeMode;
      panel.hidden = !isActive;
      panel.querySelectorAll('select, input, button').forEach((control) => {
        if (control.matches('[type="submit"]')) control.disabled = !isActive;
        else if (control.dataset.originalDisabled !== 'true') control.disabled = !isActive;
      });
    });
  };

  const setMode = (mode, shouldFocus = false) => {
    activeMode = mode === 'shocks' ? 'shocks' : 'system';
    root.querySelectorAll('[data-trm-mode]').forEach((button) => {
      button.setAttribute('aria-selected', String(button.dataset.trmMode === activeMode));
    });
    enablePanelFields();
    strategy.hidden = true;
    results.hidden = true;
    form.hidden = false;
    if (shouldFocus) {
      root.querySelector(`[data-trm-mode-panel="${activeMode}"] .trm-suspension__question`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const architectureCopy = {
    spacer: {
      title: 'Spacer / puck level',
      summary: 'Use the spacer to change stance while keeping the original spring and damper behavior as the baseline.',
      wins: ['Lowest entry cost', 'Keeps the original spring rate', 'Good when stance is the main goal'],
      watch: ['Does not add damping or heat capacity', 'Confirm droop, CV and ball-joint angles', 'Alignment is still required']
    },
    strut: {
      title: 'Complete leveling strut',
      summary: 'A complete strut or height-adjustable strut combines the height change with fresh, application-tuned damping.',
      wins: ['Height and damping addressed together', 'Cleaner replacement when stock struts are tired', 'Strong daily-driver balance'],
      watch: ['Higher settings can reduce droop', 'Alignment and upper-control-arm clearance still matter', 'Do not choose height by tire size alone']
    },
    spring: {
      title: 'Full-length spring + matched shock',
      summary: 'A spring-and-damper package is the better tool when load, travel or ride character must change—not just the stance.',
      wins: ['Spring rate can match real truck weight', 'Matched shock length and control', 'Better path for loaded travel and work use'],
      watch: ['Constant accessory weight must be known', 'Spring rate and shock tune must agree', 'Front and rear height should be planned separately']
    },
    coilover: {
      title: 'Coilover system',
      summary: 'A coilover gives the most integrated control of ride height, spring support and damping for a performance-focused build.',
      wins: ['Height, spring and damping in one package', 'Strong tuning and rebuild potential', 'Best fit for repeated hard use'],
      watch: ['Preload is not unlimited free lift', 'More cost and service responsibility', 'Geometry and travel limits remain vehicle-specific']
    },
    complete: {
      title: 'Complete suspension lift system',
      summary: 'At four inches and above, the reliable path is an engineered system that treats brackets, steering and suspension geometry together.',
      wins: ['Designed around larger height changes', 'Addresses more of the geometry as a system', 'Clearer path to larger tire packages'],
      watch: ['More labor and alignment work', 'Driveshaft, brake-line and steering checks may be required', 'Shock length must match the actual kit architecture']
    }
  };

  const chooseArchitecture = (fields) => {
    if (fields.system_architecture && fields.system_architecture !== 'help') return fields.system_architecture;
    const height = Number(fields.system_height || 0);
    if (height >= 4) return 'complete';
    if (fields.system_goal === 'travel' || fields.system_use === 'fast') return fields.system_budget === 'premium' ? 'coilover' : 'spring';
    if (fields.system_goal === 'load' || fields.system_use === 'tow' || fields.system_use === 'overland') return 'spring';
    if (fields.system_goal === 'ride') return height <= 2.5 ? 'strut' : 'spring';
    if (height <= 2 && fields.system_budget === 'value') return 'spacer';
    return 'strut';
  };

  const renderStrategyCard = (label, title, copy, bullets, primary = false) => `
    <article class="trm-suspension__strategy-card${primary ? ' is-primary' : ''}">
      <small>${escapeHtml(label)}</small>
      <h3>${escapeHtml(title)}</h3>
      <p>${escapeHtml(copy)}</p>
      <ul>${bullets.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>
    </article>`;

  const renderStrategy = (fields) => {
    const vehicle = readVehicle();
    const architecture = chooseArchitecture(fields);
    const selected = architectureCopy[architecture];
    const height = Number(fields.system_height || 0);
    const useLabels = { daily: 'daily driving', tow: 'towing and hauling', overland: 'loaded overland travel', trail: 'technical trail use', fast: 'repeated faster dirt-road use' };
    const goalLabels = { level: 'level the stance', tires: 'create tire clearance', ride: 'improve ride control', travel: 'gain usable travel', load: 'control load and payload' };
    const damper = (fields.system_use === 'fast' || fields.system_budget === 'premium')
      ? 'Reservoir or adjustable performance shock'
      : (fields.system_use === 'tow' || fields.system_goal === 'ride')
        ? 'Vehicle-tuned monotube / IFP shock'
        : 'Application-matched shock or strut';
    const damperBullets = fields.system_use === 'fast'
      ? ['Prioritize oil volume and heat capacity', 'Use compression adjustment only if you will tune it', 'Match front and rear damping to the complete build']
      : ['Match the actual lift range at each axle', 'Do not use a stiffer shock to replace missing spring support', 'Confirm whether the listing is one shock, a pair or a complete set'];
    const geometryBullets = height >= 4
      ? ['Verify control-arm, knuckle or bracket architecture', 'Check brake lines, driveshafts, steering and bump stops', 'Align after installation and re-torque after the initial service interval']
      : ['Confirm alignment range and upper-control-arm clearance', 'Check CV and ball-joint angles at full droop', 'Verify tire clearance at steering lock and suspension compression'];

    root.querySelector('[data-trm-strategy-title]').textContent = selected.title;
    root.querySelector('[data-trm-strategy-lead]').textContent = `${vehicle ? vehicle.label + ': ' : ''}To ${goalLabels[fields.system_goal] || 'upgrade the suspension'} for ${useLabels[fields.system_use] || 'your use'}, start with ${selected.title.toLowerCase()}. ${selected.summary}`;
    root.querySelector('[data-trm-strategy-grid]').innerHTML = [
      renderStrategyCard('Best starting architecture', selected.title, selected.summary, selected.wins, true),
      renderStrategyCard('Damper direction', damper, 'The shock controls motion and heat; it does not carry the truck or create the spring rate.', damperBullets),
      renderStrategyCard('Installation reality check', 'Protect the geometry', 'The same advertised height can behave differently depending on how the kit creates it.', geometryBullets)
    ].join('');
    renderLiftResults(fields);
    form.hidden = true;
    results.hidden = true;
    strategy.hidden = false;
    strategy.focus({ preventScroll: true });
    strategy.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const parseLiftRange = (product) => {
    const raw = String(product.title || '').toLowerCase();
    if (/stock height|\b0in\b|\b0 inch/.test(raw) && !/\d\s*[-–/]\s*\d/.test(raw)) return { min: 0, max: 0, explicit: true };
    const ranges = [...raw.matchAll(/(\d+(?:\.\d+)?)\s*(?:-|–|to|\/)\s*(\d+(?:\.\d+)?)\s*(?:in(?:ch(?:es)?)?|[\"”])/g)]
      .map((match) => [Number(match[1]), Number(match[2])])
      .filter(([min, max]) => min <= 12 && max <= 12);
    if (ranges.length) {
      const values = ranges.flat();
      return { min: Math.min(...values), max: Math.max(...values), explicit: true };
    }
    const singles = [...raw.matchAll(/(?:\b|\+)(\d+(?:\.\d+)?)\s*(?:in(?:ch(?:es)?)?|[\"”])/g)]
      .map((match) => Number(match[1]))
      .filter((value) => value <= 12);
    if (singles.length) return { min: Math.min(...singles), max: Math.max(...singles), explicit: true };
    if (/bilstein\s+(?:b6\s+)?4600/.test(raw)) return { min: 0, max: 0, explicit: true };
    return { min: null, max: null, explicit: false };
  };

  const parseAxle = (product) => {
    const raw = normalize(product.title);
    const front = /\bfront\b/.test(raw);
    const rear = /\brear\b/.test(raw);
    if (front && rear) return 'all';
    if (front) return 'front';
    if (rear) return 'rear';
    return 'unknown';
  };

  const parseTech = (product) => {
    const raw = normalize(`${product.title} ${product.description || ''} ${(product.tags || []).join(' ')}`);
    const reservoir = /reservoir|remote res|piggyback|external res|\bresi\b/.test(raw);
    const adjustable = /adjustable|\bdsc\b|\bcdcv\b|compression adjust|ride height adjust|coilover/.test(raw);
    const monotube = /monotube|\bifp\b|46mm|2\.0|2\.25|2\.5|3\.0/.test(raw) || reservoir;
    return { reservoir, adjustable, monotube };
  };

  const priceTier = (price) => price < 250 ? 'value' : price < 900 ? 'performance' : 'premium';

  const modelFamily = (value) => {
    const raw = normalize(value);
    const families = [
      ['f150', /\bf\s*150\b/],
      ['f250', /\bf\s*250\b/],
      ['f350', /\bf\s*350\b/],
      ['f450', /\bf\s*450\b/],
      ['f550', /\bf\s*550\b/],
      ['silverado1500', /\bsilverado\s*1500\b/],
      ['silverado2500', /\bsilverado\s*2500\b/],
      ['silverado3500', /\bsilverado\s*3500\b/],
      ['sierra1500', /\bsierra\s*1500\b/],
      ['sierra2500', /\bsierra\s*2500\b/],
      ['sierra3500', /\bsierra\s*3500\b/],
      ['ram1500', /\b(?:ram|dodge ram)\s*1500\b/],
      ['ram2500', /\b(?:ram|dodge ram)\s*2500\b/],
      ['ram3500', /\b(?:ram|dodge ram)\s*3500\b/],
      ['tacoma', /\btacoma\b/],
      ['tundra', /\btundra\b/],
      ['4runner', /\b4\s*runner\b/],
      ['sequoia', /\bsequoia\b/],
      ['ranger', /\branger\b/],
      ['bronco', /\bbronco\b/],
      ['colorado', /\bcolorado\b/],
      ['canyon', /\bcanyon\b/],
      ['wrangler', /\bwrangler\b/],
      ['gladiator', /\bgladiator\b/],
      ['frontier', /\bfrontier\b/],
      ['titan', /\btitan\b/],
      ['grandcherokee', /\bgrand\s+cherokee\b/],
      ['cherokee', /\bcherokee\b/],
      ['tahoe', /\btahoe\b/],
      ['yukon', /\byukon\b/],
      ['suburban', /\bsuburban\b/],
      ['avalanche', /\bavalanche\b/],
      ['expedition', /\bexpedition\b/],
      ['excursion', /\bexcursion\b/],
      ['explorer', /\bexplorer\b/],
      ['escalade', /\bescalade\b/],
      ['xterra', /\bxterra\b/],
      ['landcruiser', /\bland\s+cruiser\b/],
      ['gx', /\b(?:lexus\s+)?gx\s*(?:460|470|550)?\b/]
    ];
    return families.find(([, pattern]) => pattern.test(raw))?.[0] || '';
  };

  const makeFamily = (value) => {
    const raw = normalize(value);
    if (/\bford\b/.test(raw)) return 'ford';
    if (/\b(?:chevy|gmc|gm)\b/.test(raw)) return 'gm';
    if (/\b(?:ram|dodge)\b/.test(raw)) return 'ram';
    if (/\btoyota\b/.test(raw)) return 'toyota';
    if (/\bjeep\b/.test(raw)) return 'jeep';
    if (/\bnissan\b/.test(raw)) return 'nissan';
    return '';
  };

  const titleYearWindows = (value) => {
    const raw = String(value || '').toLowerCase();
    const expand = (number) => {
      const year = Number(number);
      if (year >= 1000) return year;
      return year >= 80 ? 1900 + year : 2000 + year;
    };
    const windows = [...raw.matchAll(/\b(\d{2}|\d{4})\s*[-–]\s*(\d{2}|\d{4})\b/g)]
      .map((match) => ({ min: expand(match[1]), max: expand(match[2]) }))
      .filter((range) => range.min >= 1980 && range.max <= 2099 && range.min <= range.max);
    [...raw.matchAll(/\b(\d{2}|\d{4})\s*\+(?=\s|$)/g)].forEach((match) => {
      const min = expand(match[1]);
      if (min >= 1980 && min <= 2099) windows.push({ min, max: 2099 });
    });
    [...raw.matchAll(/\b((?:19|20)\d{2})\b/g)].forEach((match) => {
      const year = Number(match[1]);
      if (!windows.some((range) => year >= range.min && year <= range.max)) windows.push({ min: year, max: year });
    });
    return windows;
  };

  const driveEvidence = (value) => {
    const raw = normalize(value);
    const two = /\b2wd\b|\b2\s*wheel\s*drive\b|\b4x2\b/.test(raw);
    const four = /\b4wd\b|\bawd\b|\b4\s*wheel\s*drive\b|\b4x4\b/.test(raw);
    return { two, four, both: two && four };
  };

  const specialModel = (value) => {
    const raw = normalize(value);
    const terms = [
      ['raptor', /\braptor\b/], ['tremor', /\btremor\b/], ['powerboost', /\bpowerboost\b/],
      ['zr2', /\bzr2\b/], ['at4x', /\bat4x\b/], ['power wagon', /\bpower\s+wagon\b/],
      ['trx', /\btrx\b/], ['trd pro', /\btrd\s+pro\b/]
    ];
    return terms.find(([, pattern]) => pattern.test(raw))?.[0] || '';
  };

  const vehicleProfile = (vehicle, fields = {}) => {
    if (!vehicle) return null;
    const raw = normalize(`${vehicle.key || ''} ${vehicle.label || ''}`);
    const year = String(fields.exact_year || raw.match(/\b(19|20)\d{2}\b/)?.[0] || '');
    const make = makeFamily(raw);
    const family = modelFamily(raw);
    const ignored = new Set(['all','trims','trim','plus','the',year,make]);
    const modelTokens = raw.split(' ').filter((token) => token.length > 1 && !ignored.has(token) && !/^\d{4}$/.test(token));
    return { year, make, family, drive: fields.fitment_drive || '', modelTokens, raw };
  };

  const vehicleScore = (product, profile) => {
    if (!profile) return { match: true, score: 0, exact: false };
    const title = normalize(product.title);
    const titleFamily = modelFamily(title);
    const titleMake = makeFamily(title);
    const titleYears = titleYearWindows(product.title);
    const profileYear = Number(profile.year || 0);
    if (profile.make && titleMake && titleMake !== profile.make) return { match: false, score: 0, exact: false };
    if (profile.family && titleFamily && titleFamily !== profile.family) return { match: false, score: 0, exact: false };
    if (profileYear && titleYears.length && !titleYears.some((range) => profileYear >= range.min && profileYear <= range.max)) {
      return { match: false, score: 0, exact: false };
    }
    const tags = (product.tags || [])
      .filter((tag) => /^\d{4}-|^vehicle:/i.test(tag))
      .map((tag) => normalize(tag.replace(/-esi\d+$/i, '')));
    const familyTags = profile.family ? tags.filter((tag) => modelFamily(tag) === profile.family) : tags;
    const haystack = normalize(`${familyTags.join(' ')} ${title}`);
    const yearMatch = !profile.year || titleYears.some((range) => profileYear >= range.min && profileYear <= range.max) || haystack.includes(profile.year);
    const makeMatch = !profile.make || titleMake === profile.make || familyTags.some((tag) => makeFamily(tag) === profile.make);
    const familyMatch = !profile.family || titleFamily === profile.family || familyTags.length > 0;
    const tokenHits = profile.modelTokens.filter((token) => haystack.includes(token)).length;
    const fallbackNeed = Math.min(2, Math.max(1, profile.modelTokens.length));
    const fallbackMatch = !profile.family && tokenHits >= fallbackNeed;
    const match = yearMatch && makeMatch && (profile.family ? familyMatch : fallbackMatch);
    return { match, score: match ? 58 + Math.min(tokenHits, 4) * 3 + (titleFamily === profile.family ? 8 : 0) : 0, exact: match };
  };

  const shockWhy = (product, tech, use, lift) => {
    if (tech.reservoir && (use === 'fast' || use === 'trail')) return 'Reservoir construction adds oil and gas volume for repeated rough-road cycling and heat control.';
    if (tech.adjustable) return 'External adjustment or coilover architecture adds tuning range for changing load and terrain.';
    if (tech.monotube && use === 'tow') return 'Monotube / IFP construction is a strong direction for consistent body control under load.';
    if (tech.monotube) return 'Monotube / IFP construction provides direct response and separates oil from the gas charge.';
    if (lift.explicit) return 'The current catalog title exposes a numeric lift range that covers the selected height.';
    return 'The vehicle and axle context match, but the numeric lift range should be confirmed by part number.';
  };

  const shockMatches = (fields, relax = false) => {
    const targetHeight = Number(fields.shock_height);
    const targetWindow = ({
      '0': { min: -.1, max: .25 }, '1.5': { min: .75, max: 2.25 },
      '2.5': { min: 1.75, max: 3.25 }, '3.5': { min: 2.75, max: 4.25 },
      '5': { min: 3.75, max: 6.25 }, '7': { min: 5.75, max: 8.25 }
    })[String(fields.shock_height)] || { min: targetHeight - .15, max: targetHeight + .15 };
    const targetAxle = fields.shock_axle || 'all';
    const targetTech = fields.shock_tech || 'all';
    const targetBudget = fields.shock_budget || 'all';
    const targetBrand = fields.shock_brand || 'all';
    const use = fields.shock_use || 'daily';
    const profile = vehicleProfile(readVehicle(), fields);

    const matches = index.map((product) => {
      const lift = parseLiftRange(product);
      const axle = parseAxle(product);
      const tech = parseTech(product);
      const tier = priceTier(Number(product.price || 0));
      const vehicle = vehicleScore(product, profile);
      if (profile && !vehicle.match) return null;
      const drive = driveEvidence(product.title);
      if (profile?.drive === 'unknown' && !drive.both) return null;
      if (profile?.drive === '2wd' && !drive.two) return null;
      if (profile?.drive === '4wd' && !drive.four) return null;
      if (!relax && targetBrand !== 'all' && normalize(product.vendor) !== normalize(targetBrand)) return null;
      if (targetAxle !== 'all' && axle !== 'all' && axle !== targetAxle) return null;
      if (targetHeight > 0 && !lift.explicit) return null;
      if (lift.explicit && (lift.max < targetWindow.min || lift.min > targetWindow.max)) return null;
      if (!relax && targetBudget !== 'all' && tier !== targetBudget) return null;
      if (!relax && targetTech === 'reservoir' && !tech.reservoir) return null;
      if (!relax && targetTech === 'adjustable' && !tech.adjustable) return null;
      if (!relax && targetTech === 'monotube' && !tech.monotube) return null;
      let score = vehicle.score;
      score += lift.explicit ? 32 : 5;
      score += targetAxle === 'all' ? 2 : (axle === targetAxle ? 12 : 3);
      if ((use === 'fast' || use === 'trail') && tech.reservoir) score += 15;
      if (use === 'tow' && tech.monotube) score += 11;
      if (use === 'daily' && tier === 'value') score += 7;
      if (targetTech === 'reservoir' && tech.reservoir) score += 12;
      if (targetTech === 'adjustable' && tech.adjustable) score += 12;
      if (targetTech === 'monotube' && tech.monotube) score += 10;
      score += Math.min(8, Math.log10(Math.max(1, product.inventory)) * 2);
      return { product, lift, axle, tech, tier, score, why: shockWhy(product, tech, use, lift), vehicleExact: vehicle.exact };
    }).filter(Boolean).sort((a, b) => b.score - a.score || b.product.inventory - a.product.inventory || a.product.price - b.product.price);
    return uniqueCatalogMatches(matches);
  };

  const liftLabel = (lift) => {
    if (!lift.explicit) return 'Height confirmation needed';
    if (lift.min === lift.max) return lift.min === 0 ? 'Stock height' : `${lift.min} in lift`;
    return `${lift.min}–${lift.max} in lift`;
  };

  const techBadges = (match) => {
    const badges = [liftLabel(match.lift)];
    if (match.axle !== 'unknown') badges.push(match.axle === 'all' ? 'Front + rear' : match.axle[0].toUpperCase() + match.axle.slice(1));
    if (match.tech.reservoir) badges.push('Reservoir');
    else if (match.tech.monotube) badges.push('Monotube / IFP');
    if (match.tech.adjustable) badges.push('Adjustable');
    return badges.slice(0, 4);
  };

  const liftHeightWindow = (choice) => ({
    '0': { min: -.1, max: .25 },
    '1.5': { min: .75, max: 2.25 },
    '2.5': { min: 1.75, max: 3.25 },
    '4': { min: 3.25, max: 4.75 },
    '6': { min: 5, max: 6.75 },
    '8': { min: 7, max: 10 }
  }[String(choice)] || null);

  const architectureScore = (product, architecture) => {
    const raw = normalize(product.title);
    const patterns = {
      spacer: /spacer|puck|leveling kit/,
      strut: /strut|leveling kit/,
      spring: /coil spring|leaf spring|spring and shock/,
      coilover: /coilover/,
      complete: /complete|suspension system|lift kit/
    };
    return patterns[architecture]?.test(raw) ? 14 : 0;
  };

  const liftMatches = (fields) => {
    const profile = vehicleProfile(readVehicle(), fields);
    const heightWindow = liftHeightWindow(fields.system_height);
    const targetBrand = fields.system_brand || 'all';
    const targetDrive = fields.fitment_drive || '';
    if (!profile?.year || !profile.family || !heightWindow || !targetDrive) return [];

    const matches = liftIndex.map((product) => {
      const raw = normalize(product.title);
      if (/\bbody lift\b/.test(raw)) return null;
      if (!/\blift\b|\blevel(?:ing)?\b/.test(raw)) return null;
      if (/control arm kit|upper control arm|shock extension|u bolt kit|lift blocks?\b/.test(raw) && !/lift kit|suspension system/.test(raw)) return null;
      const titleFamily = modelFamily(product.title);
      if (!titleFamily || titleFamily !== profile.family) return null;
      const profileSpecial = specialModel(profile.raw);
      const productSpecial = specialModel(product.title);
      if (productSpecial && productSpecial !== profileSpecial) return null;
      if (profileSpecial && productSpecial !== profileSpecial) return null;
      const titleMake = makeFamily(product.title);
      if (profile.make && titleMake && titleMake !== profile.make) return null;
      const years = titleYearWindows(product.title);
      const exactYear = Number(profile.year);
      if (!years.length || !years.some((range) => exactYear >= range.min && exactYear <= range.max)) return null;
      const drive = driveEvidence(product.title);
      if (targetDrive === 'unknown' && !drive.both) return null;
      if (targetDrive === '2wd' && !drive.two) return null;
      if (targetDrive === '4wd' && !drive.four) return null;
      const lift = parseLiftRange(product);
      if (!lift.explicit || lift.max < heightWindow.min || lift.min > heightWindow.max) return null;
      if (targetBrand !== 'all' && normalize(product.vendor) !== normalize(targetBrand)) return null;
      const tier = priceTier(Number(product.price || 0));
      let score = 100 + architectureScore(product, fields.system_architecture);
      if (/complete|suspension system/.test(raw)) score += 6;
      if (/with shocks|incl shocks|coilover|strut/.test(raw)) score += fields.system_goal === 'ride' ? 8 : 3;
      if (fields.system_budget === tier) score += 10;
      score += Math.min(8, Math.log10(Math.max(1, product.inventory || 0)) * 2);
      return { product, lift, tier, score, exactYear, drive };
    }).filter(Boolean).sort((a, b) => b.score - a.score || b.product.inventory - a.product.inventory || a.product.price - b.product.price);
    return uniqueCatalogMatches(matches);
  };

  const renderLiftCard = (match) => {
    const product = match.product;
    const safeImage = product.image && !/(warning|prop[-_ ]?65|cancer|reproduction|logo|brand[-_ ]?mark)/i.test(product.image)
      ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" width="600" height="450" loading="lazy">`
      : '<span aria-hidden="true" style="font-size:56px;color:#c7ccd0">↕</span>';
    const price = new Intl.NumberFormat('en-US', { style: 'currency', currency: product.currency || 'USD', maximumFractionDigits: 2 }).format(Number(product.price || 0));
    const driveLabel = match.drive.both ? '2WD + 4WD stated' : match.drive.four ? '4WD stated' : '2WD stated';
    return `
      <article class="trm-shock-card">
        <div class="trm-shock-card__media">${safeImage}<span class="trm-shock-card__tier">${escapeHtml(match.tier)} class</span><span class="trm-shock-card__stock">In stock</span></div>
        <div class="trm-shock-card__body">
          <span class="trm-shock-card__vendor">${escapeHtml(product.vendor)}</span>
          <h3>${escapeHtml(product.title)}</h3>
          <div class="trm-shock-card__badges"><span>${escapeHtml(liftLabel(match.lift))}</span><span>${escapeHtml(String(match.exactYear))} covered</span><span>${escapeHtml(driveLabel)}</span></div>
          <p class="trm-shock-card__why">The listing title states the selected model family, a model-year window covering ${escapeHtml(match.exactYear)}, the drivetrain and a lift range overlapping your target. Confirm trim, cab, axle and kit contents on the product page.</p>
          <div class="trm-shock-card__footer"><span class="trm-shock-card__price">${escapeHtml(price)}<small>Current listing price</small></span><a href="${escapeHtml(product.url)}">Review fitment →</a></div>
        </div>
      </article>`;
  };

  const renderLiftResults = (fields) => {
    const matches = liftMatches(fields);
    const shortlist = matches.slice(0, 9);
    const vehicle = readVehicle();
    const title = root.querySelector('[data-trm-lift-results-title]');
    const copy = root.querySelector('[data-trm-lift-results-copy]');
    const grid = root.querySelector('[data-trm-lift-grid]');
    if (title) title.textContent = shortlist.length ? `${shortlist.length} fitment-evidenced lift kits` : 'No lift kit clears every checkpoint yet';
    if (copy) copy.textContent = shortlist.length
      ? `${vehicle?.label || 'Selected truck'}, exact model year ${fields.exact_year}, ${fields.fitment_drive.toUpperCase()} and ${fields.system_height} in target. Results require model, year, drivetrain and numeric height evidence in the listing title; brand is applied only after those conditions.`
      : `${vehicle?.label || 'Selected truck'}: no in-stock listing currently states all selected model, year, drivetrain and height conditions. Browse the saved-truck collection or ask Tex to verify a manufacturer part number—we do not loosen fitment to fill the grid.`;
    if (grid) grid.innerHTML = shortlist.length
      ? shortlist.map(renderLiftCard).join('')
      : '<div class="trm-suspension__no-results"><h3>No guesswork added.</h3><p>Try another target height or select “show every approved brand.” If the grid remains empty, use the saved-truck catalog link below and verify the manufacturer application before purchase.</p></div>';
  };

  const renderShockCard = (match) => {
    const product = match.product;
    const image = product.image
      ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" width="600" height="450" loading="lazy">`
      : '<span aria-hidden="true" style="font-size:56px;color:#c7ccd0">↕</span>';
    const price = new Intl.NumberFormat('en-US', { style: 'currency', currency: product.currency || 'USD', maximumFractionDigits: 2 }).format(Number(product.price || 0));
    return `
      <article class="trm-shock-card">
        <div class="trm-shock-card__media">
          ${image}
          <span class="trm-shock-card__tier">${escapeHtml(match.tier)} class</span>
          <span class="trm-shock-card__stock">In stock</span>
        </div>
        <div class="trm-shock-card__body">
          <span class="trm-shock-card__vendor">${escapeHtml(product.vendor)}</span>
          <h3>${escapeHtml(product.title)}</h3>
          <div class="trm-shock-card__badges">${techBadges(match).map((badge) => `<span>${escapeHtml(badge)}</span>`).join('')}</div>
          <p class="trm-shock-card__why">${escapeHtml(match.why)}</p>
          <div class="trm-shock-card__footer">
            <span class="trm-shock-card__price">${escapeHtml(price)}<small>Current listing price</small></span>
            <a href="${escapeHtml(product.url)}">View product →</a>
          </div>
        </div>
      </article>`;
  };

  const renderShockResults = (fields) => {
    let matches = shockMatches(fields, false);
    let relaxed = false;
    if (!matches.length && (fields.shock_budget !== 'all' || fields.shock_tech !== 'all' || fields.shock_brand !== 'all')) {
      matches = shockMatches(fields, true);
      relaxed = true;
    }
    const shortlist = matches.slice(0, 12);
    const vehicle = readVehicle();
    const heightText = ({ '0':'stock height', '1.5':'1–2 in', '2.5':'2–3 in', '3.5':'3–4 in', '5':'4–6 in', '7':'6–8 in' })[fields.shock_height] || `${fields.shock_height} in`;
    const exactYear = fields.exact_year;
    const brandText = fields.shock_brand && fields.shock_brand !== 'all' ? fields.shock_brand : 'all approved brands';
    const axleText = fields.shock_axle === 'all' ? 'front and rear' : fields.shock_axle;
    root.querySelector('[data-trm-shock-results-title]').textContent = shortlist.length
      ? `${shortlist.length} strong catalog match${shortlist.length === 1 ? '' : 'es'}`
      : 'No exact indexed match yet';
    root.querySelector('[data-trm-shock-results-copy]').textContent = shortlist.length
      ? `${vehicle ? vehicle.label + ', ' : ''}model year ${exactYear}, ${axleText}, ${heightText} suspension lift. Products with an explicit lift range rank above listings that still need height confirmation.${relaxed ? ' The requested price, brand or technology preference was relaxed because no exact result remained.' : ''}`
      : `${vehicle ? vehicle.label + ': ' : ''}The complete saved-truck catalog was checked, but no available listing states every selected year, drivetrain, axle and height condition.`;
    root.querySelector('[data-trm-shock-summary]').innerHTML = [
      vehicle ? vehicle.label : 'Truck not selected',
      `Model year ${exactYear}`,
      `${heightText} lift`,
      `${axleText} axle`,
      `${fields.shock_use} use`,
      relaxed ? 'Closest brand / technology / price class' : `${brandText} · ${fields.shock_budget} class`
    ].map((item) => `<span>${escapeHtml(item)}</span>`).join('');
    root.querySelector('[data-trm-shock-grid]').innerHTML = shortlist.length
      ? shortlist.map(renderShockCard).join('')
      : `<div class="trm-suspension__no-results"><h3>${fields.shock_axle === 'front' ? 'This front position may use a strut or coilover—not a conventional shock.' : 'Keep the vehicle, verify the part.'}</h3><p>${fields.shock_axle === 'front' ? 'Try the complete lift/level path for a matched front strut or coilover, or change only the height if the truck already has a different lift system. We will not substitute a rear shock or a part with an unstated lift range.' : 'Change the height or technology preference, or ask Tex to verify a manufacturer part number. We will not call a shock compatible when the listing does not support the selected axle and height.'}</p></div>`;
    form.hidden = true;
    strategy.hidden = true;
    results.hidden = false;
    results.focus({ preventScroll: true });
    results.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const editMode = (mode) => {
    setMode(mode);
    strategy.hidden = true;
    results.hidden = true;
    form.hidden = false;
    form.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  root.querySelectorAll('[data-trm-mode]').forEach((button) => button.addEventListener('click', () => setMode(button.dataset.trmMode)));
  root.querySelectorAll('[data-trm-mode-jump]').forEach((link) => link.addEventListener('click', () => setMode(link.dataset.trmModeJump)));
  root.querySelector('[data-trm-suspension-garage-open]')?.addEventListener('click', (event) => {
    window.dispatchEvent(new CustomEvent('trm:garage-open', { detail: { trigger: event.currentTarget, source: 'suspension-desk-v20' } }));
  });
  root.querySelector('[data-trm-edit-strategy]')?.addEventListener('click', () => editMode('system'));
  root.querySelector('[data-trm-edit-shocks]')?.addEventListener('click', () => editMode('shocks'));
  root.querySelector('[data-trm-suspension-tex]')?.addEventListener('click', () => {
    window.dispatchEvent(new CustomEvent('trm:tex-open', { detail: { source: 'suspension-desk-v20', finderKey: 'SUSPENSION' } }));
    const chat = document.querySelector('shopify-chat');
    if (chat && typeof chat.open === 'function') chat.open();
  });

  form?.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!readVehicle()) {
      setCatalogStatus('Choose a truck before product matching. The editorial guide remains available without it.', 'error');
      window.dispatchEvent(new CustomEvent('trm:garage-open', { detail: { trigger: event.submitter, source: 'suspension-desk-v20' } }));
      return;
    }
    const invalid = [...form.querySelectorAll('[required]:not(:disabled)')].find((field) => !field.checkValidity());
    if (invalid) { invalid.reportValidity(); invalid.focus(); return; }
    const fields = Object.fromEntries(new FormData(form).entries());
    const submit = form.querySelector(`[data-trm-mode-panel="${activeMode}"] [type="submit"]`);
    const original = submit?.innerHTML;
    if (submit) { submit.disabled = true; submit.textContent = 'Loading your truck catalog…'; }
    await loadVehicleCatalog(readVehicle(), true);
    if (activeMode === 'shocks') {
      const strict = shockMatches(fields, false);
      const canRelax = fields.shock_budget !== 'all' || fields.shock_tech !== 'all' || fields.shock_brand !== 'all';
      if (!strict.length && !(canRelax && shockMatches(fields, true).length)) {
        await loadVehicleCatalog(readVehicle(), true, true);
      }
    }
    if (submit) { submit.disabled = false; submit.innerHTML = original; }
    if (activeMode === 'shocks') renderShockResults(fields);
    else renderStrategy(fields);
  });

  root.querySelector('[data-trm-suspension-exact-year]')?.addEventListener('change', (event) => {
    if (event.currentTarget.value) saveVehicleDetail({ exactYear: event.currentTarget.value });
    syncVehicle();
  });
  root.querySelector('[name="fitment_drive"]')?.addEventListener('change', (event) => {
    if (event.currentTarget.value) saveVehicleDetail({ drivetrain: event.currentTarget.value });
    syncVehicle();
  });
  window.addEventListener('trm:vehicle-changed', syncVehicle);
  window.addEventListener('trm:vehicle-detail-changed', syncVehicle);
  window.addEventListener('trm:vehicle-cleared', syncVehicle);
  syncVehicle();
  setMode('system');
})();
